import { useCallback, useContext, useMemo } from 'react';
import { createId } from '@paralleldrive/cuid2';
import type { BrandInsightsInstanceContext, Chat } from '@trinity/chat';
import { ChatContextType } from '@trinity/chat';
import { useChatUploadApi } from '@trinity/chat-components/containers/Chat.FileUpload/context';
import { UploadFilesBaseContext } from '@trinity/chat-components/containers/WorkspaceFileUpload/Context';
import type { FilesMap } from '@trinity/chat-components/containers/WorkspaceFileUpload/interfaces.base';
import { UploadFilesContainer as WorkspaceUploadContainer } from '@trinity/chat-components/containers/WorkspaceFileUpload/Upload.Container';
import { ChatStateDispatchContext } from '../Chat.State/context';
import { FileUploadEffectsContainer } from './Chat.UploadEffects.Container';
import { ActiveChatSessionContext, IsPreparingUploadContext, UploadFilesContext } from './context';
import { useScrollToBottomOfMessages } from './hooks/useScrollMessageList';

type Props = {
  children: React.ReactNode;
};

const UploadFilesContainer = (props: Props) => {
  const { usePreCheckMutation, useUploadMutation } = useChatUploadApi();
  const chat = useContext(ActiveChatSessionContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const scrollToBottom = useScrollToBottomOfMessages();
  const uploadFiles = useContext(UploadFilesBaseContext);

  const { mutateAsync: runPrecheck, ...preCheckMutation } = usePreCheckMutation();

  const { mutateAsync: registerUpload, ...uploadMutation } = useUploadMutation();

  const isPreparingUpload = useMemo(() => {
    return preCheckMutation.isPending || uploadMutation.isPending;
  }, [preCheckMutation.isPending, uploadMutation.isPending]);

  const handleUpload = useCallback(
    async (files: File[]) => {
      const currentChatSupportsUploads = resolveChatFileUploadSupport(chat);

      const chatInstanceIdentifier = currentChatSupportsUploads ? chat.identifier : createId();

      const uploadIdentifier = createId();

      const filesMap = files.reduce<FilesMap>((acc, file) => {
        const identifier = createId();
        acc[identifier] = file;
        return acc;
      }, {});

      const fileObjects = Object.keys(filesMap).map((identifier) => ({
        identifier,
        name: filesMap[identifier].name,
      }));

      const { folder, object: folderObject } = await runPrecheck({
        chatInstanceIdentifier,
        files: fileObjects,
        parentObjectId: currentChatSupportsUploads
          ? (chat.context as BrandInsightsInstanceContext.FolderWithMetadata).data.objectId
          : null,
      });

      const chatInstanceContext = currentChatSupportsUploads
        ? chat.context
        : generateNewChatContext({
            chatInstanceIdentifier,
            folderObjectId: folderObject.id,
            folderName: folder.name,
            folderId: folder.id,
          });

      const { chatInstance, fileUpload } = await registerUpload({
        identifier: uploadIdentifier,
        chatInstanceIdentifier,
        context: chatInstanceContext,
        files: fileObjects,
        brandIdentifier: chat.brandIdentifier,
      });

      if (chat.identifier !== chatInstanceIdentifier) {
        dispatch({
          type: 'new-chat-added',
          payload: {
            chatInstance,
            queries: {},
            fileUploads: [fileUpload],
            brandIdentifier: chat.brandIdentifier,
          },
        });
      } else {
        dispatch({
          type: 'file-upload-initiated',
          payload: {
            fileUpload,
          },
        });
      }

      scrollToBottom();

      uploadFiles({
        isTranscriptImport: false,
        files: filesMap,
        parentObjectId: folderObject.id,
        replaceExisting: false,
        tags: [],
        transcribeable: [],
        uploadIdentifier,
        workspaceId: folderObject.workspaceId,
        respondentCountry: '',
      });
    },
    [chat, dispatch, registerUpload, runPrecheck, scrollToBottom, uploadFiles]
  );

  return (
    <UploadFilesContext.Provider value={handleUpload}>
      <IsPreparingUploadContext.Provider value={isPreparingUpload}>
        {props.children}
      </IsPreparingUploadContext.Provider>
    </UploadFilesContext.Provider>
  );
};

type ChildrenProps = {
  children: React.ReactNode;
};

const WithState = (props: ChildrenProps) => {
  return (
    <FileUploadEffectsContainer>
      <WorkspaceUploadContainer>
        <UploadFilesContainer>{props.children}</UploadFilesContainer>
      </WorkspaceUploadContainer>
    </FileUploadEffectsContainer>
  );
};

export { WithState as UploadFilesContainer };

type CreateNewContext = {
  chatInstanceIdentifier: string;
  folderObjectId: number;
  folderName: string;
  folderId: number;
};

function resolveChatFileUploadSupport(chat: Chat.Instance): boolean {
  return chat.context.type === ChatContextType.Folder && chat.context.metadata.supportsUploads;
}

function generateNewChatContext(data: CreateNewContext): BrandInsightsInstanceContext.FolderWithMetadata {
  return {
    data: {
      objectId: data.folderObjectId,
      folderId: data.folderId,
      workspaceId: null,
    },
    metadata: {
      supportsUploads: true,
      name: data.folderName,
    },
    type: ChatContextType.Folder,
  };
}
